import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

interface Profile {
  name: string;
  title: string;
}

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  category: string;
  status: string;
  year: string;
  liveUrl: string | null;
  githubUrl: string | null;
  features: string[];
}

interface Experience {
  id: number;
  title: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string;
  technologies: string[];
  achievements: string[];
}

interface HeaderProps {
  profile: Profile;
  projects: Project[];
  experiences: Experience[];
  onUpdateProjects: (projects: Project[]) => void;
  onUpdateExperiences: (experiences: Experience[]) => void;
}

const Header: React.FC<HeaderProps> = ({ profile, projects, experiences, onUpdateProjects, onUpdateExperiences }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Experience', href: '#experience' },
    { name: 'Projects', href: '#projects' },
    { name: 'Skills', href: '#skills' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-dark-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex-shrink-0"
          >
            <button
              onClick={() => scrollToSection('#home')}
              className="flex items-center space-x-3 group"
            >
              {/* SVG Logo */}
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <defs>
                    <linearGradient id="headerLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor:"#3B82F6", stopOpacity:1}} />
                      <stop offset="50%" style={{stopColor:"#1D4ED8", stopOpacity:1}} />
                      <stop offset="100%" style={{stopColor:"#1E40AF", stopOpacity:1}} />
                    </linearGradient>
                    <linearGradient id="headerAccentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor:"#10B981", stopOpacity:1}} />
                      <stop offset="100%" style={{stopColor:"#059669", stopOpacity:1}} />
                    </linearGradient>
                  </defs>
                  
                  {/* Background Circle */}
                  <circle cx="24" cy="24" r="22" fill="url(#headerLogoGradient)" stroke="#1E40AF" strokeWidth="1.5"/>
                  
                  {/* Letter M - Simplified */}
                  <path d="M13 17 L13 31 L15.5 31 L15.5 22 L18 26 L20.5 22 L20.5 31 L23 31 L23 17 L20.5 17 L18 21 L15.5 17 L13 17 Z" fill="white"/>
                  
                  {/* Letter N - Simplified */}
                  <path d="M25 17 L25 31 L27.5 31 L27.5 22 L30.5 31 L33 31 L33 17 L30.5 17 L27.5 26 L27.5 17 L25 17 Z" fill="white"/>
                  
                  {/* Tech accent - Code brackets - Simplified */}
                  <path d="M8 12 L6 12 L6 10 L8 10 L8 12 Z M8 14 L6 14 L6 16 L8 16 L8 14 Z" fill="url(#headerAccentGradient)"/>
                  <path d="M40 10 L42 10 L42 12 L40 12 L40 10 Z M40 16 L42 16 L42 14 L40 14 L40 16 Z" fill="url(#headerAccentGradient)"/>
                  
                  {/* Small dot accent */}
                  <circle cx="38" cy="38" r="1.5" fill="url(#headerAccentGradient)"/>
                </svg>
              </div>
              {/* Logo Text */}
              <div className="hidden sm:block">
                <span className="text-xl font-display font-bold gradient-text">
                  {profile.name}
                </span>
              </div>
            </button>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className="px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 hover:text-primary-600"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md transition-colors duration-200 hover:bg-gray-800"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{
            height: isMenuOpen ? 'auto' : 0,
            opacity: isMenuOpen ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className="block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 hover:text-primary-600 w-full text-left"
              >
                {item.name}
              </button>
            ))}
          </div>
        </motion.div>
      </nav>

    </motion.header>
  );
};

export default Header;
